/**
 * Condition Engine — applies conditional-rule definitions to a form instance.
 *
 * Responsibilities:
 *   - Evaluate a ConditionalExpression against the current values map.
 *   - Apply enabled rules in priority order to derive a RenderState per field
 *     (visible / required / enabled / filtered options / defaults).
 *   - Evaluate calculated-field formulas.
 *
 * Pure functions only. The rule definitions themselves live in
 * the form builder (see `./definition-types`); this module only APPLIES them.
 *
 * Integration with the validation engine: the RenderState produced here is the
 * sole input to `detectBlockingErrors` / `detectSoftWarnings`. That means a
 * condition that flips `required` or filters options automatically changes
 * which validation rules fire — there is one shared snapshot, not two
 * independent passes.
 */
import type {
  FieldDefinition,
  ConditionalRule,
  ConditionalExpression,
  ConditionOperator,
} from './definition-types'
import type { RenderState } from './types'
import { evaluateFormula, evaluateFormulaValue } from './formula'

// Re-exported for back-compat: `evaluateFormula` historically lived here.
export { evaluateFormula, evaluateFormulaValue }

export function evalCondition(
  expr: ConditionalExpression,
  values: Record<string, unknown>,
): boolean {
  if (expr.type === 'comparison') {
    const val = values[expr.field_key]
    const op = expr.operator as ConditionOperator
    const cmpVal =
      expr.compare_to_field_key !== undefined
        ? values[expr.compare_to_field_key]
        : expr.value

    const orderedCompare = (fn: (a: number | string, b: number | string) => boolean) => {
      const a = Number(val), b = Number(cmpVal)
      if (!isNaN(a) && !isNaN(b)) return fn(a, b)
      return fn(String(val ?? ''), String(cmpVal ?? ''))
    }

    switch (op) {
      case 'is_empty':
        return (
          val === null ||
          val === undefined ||
          val === '' ||
          (Array.isArray(val) && val.length === 0)
        )
      case 'is_not_empty':
        return (
          val !== null &&
          val !== undefined &&
          val !== '' &&
          !(Array.isArray(val) && val.length === 0)
        )
      case 'is_true': return val === true
      case 'is_false': return val === false || val === null || val === undefined
      case 'equals': return val === cmpVal
      case 'not_equals': return val !== cmpVal
      case 'greater_than': return orderedCompare((a, b) => a > b)
      case 'less_than': return orderedCompare((a, b) => a < b)
      case 'greater_than_or_equal': return orderedCompare((a, b) => a >= b)
      case 'less_than_or_equal': return orderedCompare((a, b) => a <= b)
      case 'contains':
        if (typeof val === 'string') return val.includes(String(cmpVal))
        if (Array.isArray(val)) return val.includes(cmpVal)
        return false
      case 'not_contains':
        if (typeof val === 'string') return !val.includes(String(cmpVal))
        if (Array.isArray(val)) return !val.includes(cmpVal)
        return true
      case 'starts_with':
        return typeof val === 'string' && val.startsWith(String(cmpVal ?? ''))
      case 'ends_with':
        return typeof val === 'string' && val.endsWith(String(cmpVal ?? ''))
      case 'matches_pattern': {
        if (typeof val !== 'string' || !cmpVal) return false
        try {
          return new RegExp(String(cmpVal)).test(val)
        } catch {
          return false
        }
      }
      case 'contains_option':
        return Array.isArray(val) && val.includes(cmpVal)
      case 'does_not_contain_option':
        return !Array.isArray(val) || !val.includes(cmpVal)
      case 'count_at_least':
        return Array.isArray(val) && val.length >= Number(cmpVal)
      case 'count_at_most':
        return Array.isArray(val) && val.length <= Number(cmpVal)
      case 'count_equals':
        return Array.isArray(val) && val.length === Number(cmpVal)
      default:
        return false
    }
  }
  if (expr.type === 'and')
    return (expr.children ?? []).every((c) => evalCondition(c, values))
  if (expr.type === 'or')
    return (expr.children ?? []).some((c) => evalCondition(c, values))
  if (expr.type === 'not')
    return expr.child != null ? !evalCondition(expr.child, values) : false
  return false
}

export function evaluateConditionalLogic(
  fields: FieldDefinition[],
  rules: ConditionalRule[],
  values: Record<string, unknown>,
): Record<string, RenderState> {
  const states: Record<string, RenderState> = {}
  for (const f of fields) {
    states[f.field_key] = {
      visible: f.default_visibility !== 'hidden',
      required: f.is_required,
      enabled: true,
    }
  }
  const sorted = [...rules]
    .filter((r) => r.is_enabled)
    .sort((a, b) => a.priority - b.priority)
  for (const rule of sorted) {
    if (!evalCondition(rule.condition, values)) continue
    for (const action of rule.actions) {
      const state = states[action.target_field_key]
      if (!state) continue
      switch (action.type) {
        case 'show': state.visible = true; break
        case 'hide': state.visible = false; break
        case 'require': state.required = true; break
        case 'unrequire': state.required = false; break
        case 'enable': state.enabled = true; break
        case 'disable': state.enabled = false; break
        case 'filter_options': state.filteredOptions = action.param as string[]; break
        case 'set_default_value':
          if (
            values[action.target_field_key] === undefined ||
            values[action.target_field_key] === null ||
            values[action.target_field_key] === ''
          ) {
            values[action.target_field_key] = action.param
          }
          break
      }
    }
  }
  return states
}

// ─── Calculated Fields ────────────────────────────────────────────────────────
//
// The formula grammar + evaluator live in `formula.ts` (shared contract with the
// Dart implementation). This module only orchestrates the two-pass resolution so
// a calculated field can reference another calculated field.

export function computeCalculatedFields(
  fields: FieldDefinition[],
  base: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (let pass = 0; pass < 2; pass++) {
    const ctx = { ...base, ...out }
    for (const field of fields) {
      if (field.field_type !== 'calculated') continue
      const formula = field.type_config?.formula as string | undefined
      if (!formula) continue
      const result = evaluateFormulaValue(formula, ctx)
      const dp = field.type_config?.decimal_places as number | undefined
      if (result === null) {
        out[field.field_key] = ''
      } else if (typeof result === 'number') {
        out[field.field_key] = dp !== undefined ? parseFloat(result.toFixed(dp)) : result
      } else {
        out[field.field_key] = result
      }
    }
  }
  return out
}
